import React, { useLayoutEffect, useRef, useState } from "react";
import noImage from "../../../../../../../assets/images/noImage.png";
import OrderItemsModal from "../../../../../../../Components/Shared/Order/OrderItems/OrderItemsModal";

// Width estimates for fitting thumbnails: a thumbnail with a one-digit
// quantity, each extra quantity digit, and the "+N" badge.
const ITEM_WIDTH = 46;
const DIGIT_WIDTH = 6;
const MORE_WIDTH = 28;

// Same rules as the shared OrderItemsPreview: markers only on Bolt products;
// order status 4 = all fulfilled, status 5 = per item, attention items first.
const isAttentionItem = (item) =>
  !item?.plugin_item_id &&
  (item?.is_saleable == null || String(item?.is_saleable) === "0");

const itemMarker = (item, orderStatusId) => {
  if (item?.plugin_item_id) return null;
  if (orderStatusId === 4) return "fulfilled";
  if (orderStatusId === 5) {
    if (String(item?.is_saleable) === "1") return "fulfilled";
    if (isAttentionItem(item)) return "attention";
  }
  return null;
};

const itemWidth = (item) =>
  ITEM_WIDTH + DIGIT_WIDTH * (String(item?.item_quantity ?? 0).length - 1);

// How many thumbnails fit in `width`, keeping room for "+N" when some don't.
const fitCount = (items, width) => {
  if (!width) return items.length;
  const widths = items.map(itemWidth);
  if (widths.reduce((a, b) => a + b, 0) <= width) return items.length;

  let count = 0;
  let used = 0;
  while (count < items.length && used + widths[count] + MORE_WIDTH <= width) {
    used += widths[count];
    count += 1;
  }
  return Math.max(1, count);
};

const formatLb = (lb) =>
  `${lb >= 10 ? Math.round(lb) : Math.round(lb * 10) / 10} lb`;

// Product weight = weight x quantity per line, in lb (as the backend's
// warehouse order email totals it); only when every line has a weight.
const productWeight = (items) => {
  const lines = items.map(
    (item) => parseFloat(item?.weight) * Number(item?.item_quantity ?? 0),
  );
  if (!lines.length || lines.some((w) => !Number.isFinite(w) || w <= 0)) {
    return null;
  }
  return lines.reduce((a, b) => a + b, 0);
};

const Thumb = ({ item, orderStatusId }) => {
  const marker = itemMarker(item, orderStatusId);
  const image =
    item?.shopify_image || item?.products?.media?.[0]?.file || noImage;

  return (
    <span className="flex shrink-0 items-center gap-[3px]">
      <span
        title={`Product: ${item?.product_name || "-"}\nSKU: ${item?.sku || "-"}`}
        className="relative flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-md border-[1px] border-ui-border bg-ui-surface"
      >
        <img
          src={image}
          alt=""
          loading="lazy"
          className="h-full w-full object-contain"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = noImage;
          }}
        />
        {marker === "fulfilled" && (
          <span
            aria-label="Fulfilled"
            className="absolute right-0 top-0 flex h-[10px] w-[10px] items-center justify-center rounded-full bg-state-success-bg text-state-success-text"
          >
            <span className="text-[7px] font-bold leading-none">✓</span>
          </span>
        )}
        {marker === "attention" && (
          <span
            aria-label="Attention required"
            className="absolute right-0 top-0 flex h-[10px] w-[10px] items-center justify-center rounded-full bg-state-warning-bg text-state-warning-text"
          >
            <span className="text-[7px] font-bold leading-none">!</span>
          </span>
        )}
      </span>
      <span className="whitespace-nowrap">×{item?.item_quantity ?? 0}</span>
    </span>
  );
};

/**
 * Items column: product thumbnails with quantities (the name too when there
 * is one product, "+N" when they don't all fit); underneath, pieces and the
 * products' total weight. Click opens the items pop-up, as before.
 */
const ItemsCell = ({ row }) => {
  const [itemsModal, setItemsModal] = useState(false);
  const [width, setWidth] = useState(0);
  const lineRef = useRef(null);

  useLayoutEffect(() => {
    const line = lineRef.current;
    if (!line) return undefined;
    setWidth(line.clientWidth);
    if (typeof ResizeObserver === "undefined") return undefined;

    // Measured in the next frame, so a re-render caused by the width can't
    // trigger "ResizeObserver loop" warnings.
    let frame = null;
    const observer = new ResizeObserver(([entry]) => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        setWidth(entry.contentRect.width);
      });
    });
    observer.observe(line);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const orderStatusId = Number(row?.order_status_id);
  const details = row?.order_details ?? [];
  const items =
    orderStatusId === 5
      ? [...details].sort(
          (a, b) => Number(isAttentionItem(b)) - Number(isAttentionItem(a)),
        )
      : details;
  const hasItems = items.length > 0;

  const shown = items.length === 1 ? 1 : fitCount(items, width);
  const hidden = items.slice(shown);
  const pieces = items.reduce(
    (sum, item) => sum + Number(item?.item_quantity ?? 0),
    0,
  );
  const weight = productWeight(items);
  const summary = [
    `${pieces} ${pieces === 1 ? "pc" : "pcs"}`,
    weight != null && formatLb(weight),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <OrderItemsModal
        open={itemsModal}
        orderId={row?.id}
        orderCode={row?.order_id}
        orderStatusId={orderStatusId}
        onClose={() => setItemsModal(false)}
      />
      <div
        onClick={hasItems ? () => setItemsModal(true) : undefined}
        className={`flex w-full min-w-0 flex-col gap-[2px] ${hasItems ? "cursor-pointer" : ""}`}
      >
        <div
          ref={lineRef}
          className="flex w-full min-w-0 items-center gap-[6px] overflow-hidden"
        >
          {!hasItems && (
            <span className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-md border-[1px] border-ui-border bg-ui-surface">
              <img
                src={noImage}
                alt=""
                className="h-full w-full object-contain"
              />
            </span>
          )}
          {items.slice(0, shown).map((item, index) => (
            <Thumb
              key={item?.id ?? index}
              item={item}
              orderStatusId={orderStatusId}
            />
          ))}
          {items.length === 1 && (
            <span className="min-w-0 truncate font-normal text-ui-text-muted">
              {items[0]?.product_name}
            </span>
          )}
          {hidden.length > 0 && (
            <span
              title={hidden
                .map(
                  (item) =>
                    `${item?.item_quantity ?? 0} × ${item?.product_name || "-"}`,
                )
                .join("\n")}
              className="shrink-0 rounded border-[1px] border-ui-border px-[5px] font-semibold text-ui-text-muted"
            >
              +{hidden.length}
            </span>
          )}
        </div>
        {!hasItems && (
          <span className="min-w-0 truncate font-normal text-ui-text-subtle">
            No items
          </span>
        )}
        {hasItems && (
          <span
            title={`${pieces} ${pieces === 1 ? "piece" : "pieces"}${
              weight != null ? ` · ${formatLb(weight)} of products` : ""
            }`}
            className="min-w-0 truncate font-normal text-ui-text-subtle"
          >
            {summary}
          </span>
        )}
      </div>
    </>
  );
};

export default ItemsCell;
